import { useState } from 'react';
import PropTypes from 'prop-types';
import { autoFixDiagramSyntax, generateSuggestedFixes, getWorkingExamples } from '../utils/diagramFixUtils';

function ErrorDisplay({ error, parsedError, code, onCodeChange }) {
  const [showTechnical, setShowTechnical] = useState(false);
  const [fixApplied, setFixApplied] = useState(false);

  const handleAutoFix = () => {
    const result = autoFixDiagramSyntax(code);
    if (result.hasChanges) {
      onCodeChange(result.fixed);
      setFixApplied(true);
      setTimeout(() => setFixApplied(false), 3000);
    }
  };

  const handleUseExample = (exampleCode) => {
    onCodeChange(exampleCode);
  };

  const suggestions = generateSuggestedFixes(error, code);
  const examples = getWorkingExamples();

  return (
    <div className="preview-error">
      <div className="error-header">
        <strong>⚠️ Diagram Error</strong>
        {parsedError?.lineNumber && (
          <span className="error-line">Line {parsedError.lineNumber}</span>
        )}
      </div>
      
      {parsedError ? (
        <div className="error-details">
          <div className="error-message">{parsedError.userMessage}</div>
          
          {parsedError.suggestion && (
            <div className="error-suggestion">
              <strong>💡 Suggestion:</strong> {parsedError.suggestion}
            </div>
          )}

          {/* Quick Fix Button */}
          <div className="error-actions">
            <button 
              className={`btn btn--secondary btn--sm ${fixApplied ? 'btn--success' : ''}`}
              onClick={handleAutoFix}
              disabled={fixApplied}
            >
              {fixApplied ? '✅ Fixed!' : '🔧 Auto Fix'}
            </button>
          </div>
          
          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="error-suggestions">
              <strong>💡 Quick Solutions:</strong>
              <div className="suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-item">
                    <div className="suggestion-title">{suggestion.title}</div>
                    <div className="suggestion-description">{suggestion.description}</div>
                    {suggestion.example && (
                      <code className="suggestion-example">{suggestion.example}</code>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Working Examples */}
          <div className="error-examples">
            <strong>📋 Try a Working Example:</strong>
            <div className="examples-list">
              {Object.entries(examples).map(([key, example]) => (
                <button
                  key={key}
                  className="btn btn--outline btn--sm example-btn"
                  onClick={() => handleUseExample(example.code)}
                  title={`Load ${example.title} example`}
                >
                  {example.title}
                </button>
              ))}
            </div>
          </div>
          
          {/* Technical Details */}
          <div className="error-technical">
            <button 
              className="technical-toggle"
              onClick={() => setShowTechnical(!showTechnical)}
            >
              {showTechnical ? '▼' : '▶'} Technical Details
            </button>
            {showTechnical && (
              <code className="technical-details">{parsedError.originalError}</code>
            )}
          </div>
        </div>
      ) : (
        <div className="error-message">Error: {error}</div>
      )}
    </div>
  );
}

ErrorDisplay.propTypes = {
  error: PropTypes.string,
  parsedError: PropTypes.object,
  code: PropTypes.string.isRequired,
  onCodeChange: PropTypes.func.isRequired
};

export default ErrorDisplay;